import _ from "lodash";
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Form } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { logAction } from "../../../app/appActions.js";
import { CURRENCY_USD } from "../../../app/appConstants";
import { getAppFeatures, getAppUser } from "../../../app/appSelectors";
import { Loader } from "../../../components/Loader/Loader.jsx";
import useUpdateEffect from "../../../hooks/useUpdateEffect";
import { useLangContext } from "../../../providers/LangProvider/LangProviderContext.js";
import amountFormat from "../../../utils/amountFormat";
import { useBonusContext } from "../../Bonus/bonusContext";
import BonusDepositPanel from "../../Bonus/components/BonusDepositPanel/BonusDepositPanel";
import { CashierContentContainerStyled } from "../../Cashier/styled/CashierStyled";
import { useGTMEvent } from "../../GoogleTagManager/googleTagManagerHooks";
import {
    depositCreateComplete,
    depositCreateFailure,
    getDepositAvailableMethodsAction,
    getDepositFormAction,
    getDepositParamsAction
} from "../accountActions";
import {
    getDepositCreateProcess,
    getDepositCreateSuccessPayload,
    getDepositForm,
    makeSelectDepositAvailableMethods,
    makeSelectDepositParams,
    makeSelectPayoutError
} from "../accountSelectors";
import AmountField from "./AmountField";
import BillingNotAvailable from "./BillingNotAvailable";
import ChangePayment from "./ChangePayment/ChangePayment";
import DepositQRCode from "./DepositQRCode/DepositQRCode.jsx";
import PaymentForm from "./PaymentForm.jsx";
import { ChangePaymentSubmitButtonContainerStyled } from "./ChangePayment/styled/ChangePaymentStyled";
import { setWelcomeApplyBonusAction } from "../../Bonus/bonusesActions";
import { isEmailValid } from "../../../utils/escapeUtils.js";
import { roundDepositPreset } from "./paymentUtils";
import PresetButton from "../../../components/PresetButton/PresetButton";
import { useFormFieldErrors } from "../../../hooks/useFormFieldErrors";
import { DEPOSIT_FIELDS_ERRORS } from "./depositConstant";
import { usePaymentOpenModuleUrl } from "./paymentHooks";
import { DepositPresetsStyled, DepositRespawnTitleContainerStyled } from "./styled/DepositStyled";
import { getIsRespawnAvailable, getWelcomeApplyBonus } from "../../Bonus/bonusesSelectors";
import { BinIcon } from "../../../components/icon/Icon";
import { useInitialUrlSearchParams } from "../../../hooks/useInitialUrlSearchParams";

function Deposit(props) {
    const {getLangPath} = useLangContext();
    const {bonusAvailablePercent, isHasBonusAvailable} = useBonusContext();
    const {
        depositCreate,
        isSelectMethodSlider,
        isDepositQRCodeSlider,
        onDepositQRCodeUnMount,
    } = props;
    const {paymentId: urlPaymentId, amount: urlUsdAmount} = useInitialUrlSearchParams(['payment_id', 'amount']);
    const initialPaymentId = useMemo(() => parseInt(urlPaymentId), [urlPaymentId]);
    const initialUsdAmount = useMemo(() => parseFloat(urlUsdAmount), [urlUsdAmount]);

    const dispatch = useDispatch();
    const applyBonus = useSelector(getWelcomeApplyBonus);
    const depositAvailableMethods = useSelector(makeSelectDepositAvailableMethods());
    const depositCreateProcess = useSelector(getDepositCreateProcess);
    const depositCreateSuccessPayload = useSelector(getDepositCreateSuccessPayload);
    const depositForm = useSelector(getDepositForm);
    const depositParams = useSelector(makeSelectDepositParams());
    const features = useSelector(getAppFeatures);
    const payoutError = useSelector(makeSelectPayoutError());
    const user = useSelector(getAppUser);
    const isRespawnAvailable = useSelector(getIsRespawnAvailable);

    const {amount: amountError, common: commonError} = useFormFieldErrors(DEPOSIT_FIELDS_ERRORS, payoutError);
    const formRef = useRef();
    const [usdAmount, setUsdAmount] = useState(initialUsdAmount || 0);
    const [currencyNotionalAmount, setCurrencyNotionalAmount] = useState(0);
    const [editCurrencyNotionalAmount, setEditCurrencyNotionalAmount] = useState(false);
    const [initAmounts, setInitAmounts] = useState(false);
    const [formError, setFormError] = useState({});
    const [selectedPayment, setSelectedPayment] = useState(null);
    const valuesRef = useRef();
    const {handleGTMClick, pushGtmEvent} = useGTMEvent({module: 'Cashier'});
    const {
        isOpened: isDepositQRCodeOpened,
        setOpened: setDepositQRCodeOpened,
    } = usePaymentOpenModuleUrl('deposit-qr-code');

    const amount = useMemo(() => editCurrencyNotionalAmount ? currencyNotionalAmount : usdAmount,
        [currencyNotionalAmount, editCurrencyNotionalAmount, usdAmount]);
    const currencyUnit = useMemo(() => editCurrencyNotionalAmount ? selectedPayment?.currency_notional?.unit : '$',
        [editCurrencyNotionalAmount, selectedPayment]);
    const currencyNotionalRate = useMemo(() => selectedPayment?.currency_notional?.rate || 1, [selectedPayment]);
    const currencyRate = useMemo(() => editCurrencyNotionalAmount ? currencyNotionalRate : 1, [currencyNotionalRate, editCurrencyNotionalAmount]);
    const currencyIso = useMemo(() => editCurrencyNotionalAmount
            ? selectedPayment?.currency_notional?.iso
            : CURRENCY_USD,
        [editCurrencyNotionalAmount, selectedPayment]
    );

    const getCurrentCurrencyAmountByUsdParam = useCallback((value) => _.round(value / currencyRate, 2), [currencyRate]);

    const handleUsdAmountChange = useCallback((value) => {
        setUsdAmount(value);
        setCurrencyNotionalAmount(_.round(value / currencyNotionalRate, 2));
    }, [currencyNotionalRate]);

    const handleCurrencyNotionalAmountChange = useCallback((value) => {
        setCurrencyNotionalAmount(value);
        setUsdAmount(_.round(value * currencyNotionalRate, 2));
    }, [currencyNotionalRate]);

    const handleAmountChange = useCallback((value) => {
        if (editCurrencyNotionalAmount) {
            handleCurrencyNotionalAmountChange(value);
        } else {
            handleUsdAmountChange(value);
        }
    }, [editCurrencyNotionalAmount, handleCurrencyNotionalAmountChange, handleUsdAmountChange]);


    /**
     * Эффект получения параметров пополнения
     */
    useEffect(() => {
        dispatch(getDepositParamsAction());
    }, [dispatch]);

    /**
     * Эффект очистки всех данных на unMount
     */
    useEffect(() => {
        return () => {
            dispatch(depositCreateComplete());
        }
    }, [dispatch]);

    /**
     * Старт init после получения/обновления всех необходимых параметров
     */
    useEffect(() => {
        if (selectedPayment && !_.isEmpty(depositParams)) {
            setInitAmounts(false);
        }
    }, [depositParams, selectedPayment]);

    /**
     * Init: установка минимального значения депозита и валюты
     */
    useEffect(() => {
        if (!initAmounts) {
            const currentUsdAmount = _.max([depositParams.min, depositParams.default, usdAmount]);
            const isInitCurrencyNotional = !!selectedPayment?.currency_notional;
            setEditCurrencyNotionalAmount(isInitCurrencyNotional);
            if (isInitCurrencyNotional) {
                handleCurrencyNotionalAmountChange(_.ceil((currentUsdAmount / currencyNotionalRate), 0))
            } else {
                handleUsdAmountChange(currentUsdAmount);
            }
            setInitAmounts(true);
        }
    }, [depositParams, usdAmount, initAmounts, selectedPayment, currencyNotionalRate, handleCurrencyNotionalAmountChange, handleUsdAmountChange]);

    /**
     * Эффект обновления формы
     */
    useEffect(() => {
        if (selectedPayment) {
            dispatch(getDepositFormAction({
                payment: selectedPayment.id
            }));
        }
    }, [selectedPayment, dispatch, depositParams]);

    useUpdateEffect(() => {
        if (payoutError) {
            pushGtmEvent({
                event: 'errorFillingDeposit',
                error: payoutError,
                amount: usdAmount,
            })
        }
    }, [payoutError]);

    useEffect(() => {
        if (depositCreateSuccessPayload?.payment_key === 'direct_pix') {
            setDepositQRCodeOpened(true);
        }
    }, [depositCreateSuccessPayload, setDepositQRCodeOpened]);

    const onChangeFieldHandler = useCallback((field, value) => {
        setFormError(prevState => {
            const newState = {
                ...prevState,
            };
            delete newState[field];
            return newState;
        })
    }, []);

    const validateForm = useCallback(() => {
        const errors = depositForm.reduce((acc, cur) => {
            const value = valuesRef.current?.[cur.field];
            switch (cur.field) {
                case 'email':
                    if (!isEmailValid(value)) {
                        acc[cur.field] = 'error.email_not_valid';
                    }
                    break;
                default:
            }
            if (cur.input_pattern) {
                if (!new RegExp(cur.input_pattern).test(value)) {
                    acc[cur.field] = `forms.validate.${cur.input_hint}` || 'forms.validate.common';
                }
            }
            return acc;
        }, {});
        if (Object.keys(errors).length === 0) {
            dispatch(logAction({
                modal: 'cashier',
                tab: 'deposit',
                operation: 'validate',
                valid: true,
            }));
            return true;
        }
        setFormError(errors);
        dispatch(logAction({
            modal: 'cashier',
            tab: 'deposit',
            operation: 'validate',
            valid: false,
            errors,
        }));
        const modalEl = document.querySelector('.offcanvas');
        modalEl?.scrollTo(0, modalEl.scrollHeight);
        return false;
    }, [depositForm, dispatch]);

    const formSubmitHandler = useCallback((e) => {
        e.preventDefault();
        if (usdAmount < depositParams.min) {
            dispatch(depositCreateFailure('error.next_deposit'));
            return;
        }
        if (usdAmount > 0 && validateForm()) {
            const payload = {
                amount: features.deposit_currency_notional ? amount : usdAmount,
                currency: features.deposit_currency_notional ? currencyIso : CURRENCY_USD,
                payment_id: selectedPayment.id,
                payment_key: selectedPayment.key,
                payment_name: selectedPayment.label,
            };
            payload.form_data = depositForm.reduce((acc, cur) => {
                acc[cur.field] = {
                    ...cur,
                    value: valuesRef.current?.[cur.field],
                };
                return acc;
            }, {})
            dispatch(logAction({
                modal: 'cashier',
                tab: 'deposit',
                operation: 'submit',
                ...payload,
            }));
            const depositCreatePayload = {
                ...payload,
                apply_bonus: applyBonus,
                isBonusApplied: applyBonus,
                isHasBonusAvailable,
                paymentType: selectedPayment.type,
            }
            depositCreate(depositCreatePayload);
            handleGTMClick({
                amount: usdAmount,
                button: 'deposit make',
                provider: selectedPayment.label,
            });
        }
    }, [
        amount,
        applyBonus,
        currencyIso,
        depositCreate,
        depositForm,
        depositParams,
        dispatch,
        features,
        handleGTMClick,
        isHasBonusAvailable,
        selectedPayment,
        usdAmount,
        validateForm,
    ]);

    const presetClickHandler = useCallback((e) => {
        e.preventDefault();
        const value = parseInt(e.currentTarget.dataset.value) || 0;
        const amount = _.round(value * currencyRate, 2);
        dispatch(logAction({
            modal: 'cashier',
            tab: 'deposit',
            operation: 'preset',
            amount: amount,
            currency_notional: editCurrencyNotionalAmount
        }));
        handleAmountChange(value);
        handleGTMClick({button: 'preset', amount});
    }, [currencyRate, dispatch, editCurrencyNotionalAmount, handleAmountChange, handleGTMClick]);

    const onCompleteHandler = useCallback((e) => {
        e?.preventDefault();
        handleGTMClick({
            button: 'back_to_cashier',
        });
        setDepositQRCodeOpened(false);
    }, [handleGTMClick, setDepositQRCodeOpened]);

    const showDepositForm = useMemo(() => {
        if (isDepositQRCodeOpened) {
            return false;
        }
        return payoutError !== 'error.billing_not_available';
    }, [isDepositQRCodeOpened, payoutError]);

    const handleChangeBonus = useCallback((checked) => {
        dispatch(setWelcomeApplyBonusAction(checked));
    }, [dispatch]);

    const logPaymentFormHandler = useCallback((params) => {
        dispatch(logAction({
            modal: 'cashier',
            tab: 'deposit',
            operation: 'input',
            ...params,
        }));
    }, [dispatch]);

    const getDepositAvailableMethodsHandler = useCallback((payload) => {
        dispatch(getDepositAvailableMethodsAction(payload))
    }, [dispatch]);

    return <Form onSubmit={formSubmitHandler} ref={formRef}
                 className="d-flex flex-grow-1 flex-shrink-1 position-relative">
        <Loader show={depositCreateProcess}/>
        <CashierContentContainerStyled>
            {
                <DepositQRCode show={isDepositQRCodeOpened}
                               payload={depositCreateSuccessPayload}
                               onCompleteHandler={onCompleteHandler}
                               onUnMountHandler={onDepositQRCodeUnMount}
                               amount={usdAmount}
                               selectedPayment={selectedPayment}
                               setDepositQRCodeOpened={setDepositQRCodeOpened}
                               isSliderWrapper={isDepositQRCodeSlider}
                />
            }

            {
                payoutError === 'error.billing_not_available' &&
                <BillingNotAvailable/>
            }

            {
                showDepositForm &&
                <ChangePayment
                    type={'deposit'}
                    extAvailableMethods={depositAvailableMethods}
                    extGetAvailableMethods={getDepositAvailableMethodsHandler}
                    initialPaymentId={initialPaymentId}
                    isSelectMethodSlider={isSelectMethodSlider}
                    selectedPayment={selectedPayment}
                    setSelectedPayment={setSelectedPayment}
                    rightColumnComponent={
                        <div className="d-flex flex-column justify-content-between mt-3">
                            <PaymentForm formData={depositForm}
                                         valuesRef={valuesRef}
                                         onChangeField={onChangeFieldHandler}
                                         formError={formError}
                                         log={logPaymentFormHandler}/>

                            {
                                !!commonError && <div className="text-danger mb-3">{getLangPath(commonError)}</div>
                            }

                            <ChangePaymentSubmitButtonContainerStyled>
                                {isRespawnAvailable && <DepositRespawnTitleContainerStyled>
                                    <BinIcon name={'IcRebuy'}/>
                                    <span className={'ms-1'} dangerouslySetInnerHTML={{__html: getLangPath('deposit.risk_free_deposit')}}/>
                                </DepositRespawnTitleContainerStyled>}
                                <Button variant="primary" size='md' type="submit"
                                        disabled={!(selectedPayment && usdAmount > 0 && !Object.keys(formError).length)}
                                        style={{minWidth: "200px"}}>
                                    {getLangPath('deposit.button', {
                                        amount: `${currencyUnit} ${amountFormat(amount || 0, 2)}`,
                                        payment_name: selectedPayment?.label || ''
                                    })}
                                </Button>
                            </ChangePaymentSubmitButtonContainerStyled>
                        </div>
                    }
                >
                    <div>
                        <AmountField
                            className="mb-2"
                            usdAmount={usdAmount}
                            handleUsdAmountChange={handleUsdAmountChange}
                            handleCurrencyNotionalAmountChange={handleCurrencyNotionalAmountChange}
                            currencyNotionalAmount={currencyNotionalAmount}
                            setEditCurrencyNotionalAmount={setEditCurrencyNotionalAmount}
                            editCurrencyNotional={editCurrencyNotionalAmount}
                            currencyNotionalParams={selectedPayment?.currency_notional}
                            error={amountError ? getLangPath(payoutError, {
                                amount: `${currencyUnit} ${getCurrentCurrencyAmountByUsdParam(depositParams.min)}`,
                            }) : false}
                            description={!user.buyer && getLangPath('error.minimum_deposit', {
                                amount: `${currencyUnit} ${getCurrentCurrencyAmountByUsdParam(depositParams.min)}`
                            })}
                        />

                        {
                            !!depositParams.presets?.length &&
                            <DepositPresetsStyled>
                                {depositParams.presets.map(item => {
                                    const presetAmount = editCurrencyNotionalAmount
                                        ? roundDepositPreset(item / currencyNotionalRate)
                                        : item
                                    return <Fragment key={item}>
                                        <PresetButton
                                            disabled={amount?.toString() === presetAmount.toString()}
                                            onClick={presetClickHandler}
                                            value={presetAmount}
                                            title={`${currencyUnit} ${amountFormat(presetAmount, 0)}`}
                                        />
                                    </Fragment>
                                })}
                            </DepositPresetsStyled>
                        }

                        {!!bonusAvailablePercent && <div className={'mt-4'}>
                            <BonusDepositPanel
                                bonusPercent={bonusAvailablePercent}
                                depositAmount={usdAmount}
                                onChange={handleChangeBonus}
                                isBonusApplied={applyBonus}
                            />
                        </div>}
                    </div>
                </ChangePayment>
            }
        </CashierContentContainerStyled>
    </Form>
}

export default Deposit;
